import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Alert,
} from "@mui/material";
import authService from "../services/authService";

interface ForgotPasswordPageProps {
    open: boolean;
    onClose: () => void;
    onReset: (emailOrUsername: string) => void;
}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
    open,
    onClose,
    onReset,
}) => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: "success" | "error" | "info" } | null>(null);

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleReset = async () => {
        if (!email.trim()) {
            setMessage({ text: "Please enter your email.", type: "error" });
            return;
        }

        if (!validateEmail(email)) {
            setMessage({ text: "Please enter a valid email address.", type: "error" });
            return;
        }

        setLoading(true);
        setMessage(null);
        try {
            await authService.forgotPassword(email);
            setMessage({ text: "Email sent successfully", type: "success" });
            // onReset(email); // Optionally keep this if parent needs to know
            setTimeout(() => {
                handleClose();
            }, 3000);
        } catch (err: any) {
            const errorMsg = err.message || "Failed to send reset email.";
            if (errorMsg.toLowerCase().includes("not found")) {
                setMessage({ text: "This email does not exist.", type: "info" as any });
            } else {
                setMessage({ text: "This email does not exist.", type: "info" as any }); // fallback for the requested change
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setEmail("");
        setMessage(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    p: 2,
                    maxWidth: "500px", // Custom width for "slightly increased" feel
                },
            }}
        >

            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#00478f" }}>
                    Reset Password
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
                <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
                    Enter your email
                </Typography>

                {message && message.type === "success" && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2,
                            borderRadius: "8px",
                            "& .MuiAlert-message": {
                                color: "#0ea5e9",
                                fontWeight: "bold"
                            }
                        }}
                    >
                        {message.text}
                    </Alert>
                )}

                <TextField
                    autoFocus
                    margin="dense"
                    id="email"
                    placeholder="Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    disabled={loading}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (message) setMessage(null);
                    }}
                    error={message?.type === "error" || message?.type === "info"}
                    helperText={message && message.type !== "success" ? message.text : ""}
                    sx={{
                        "& .MuiFormHelperText-root": {
                            color: message?.type === "info" ? "#0066cc" : undefined,
                        },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            "& fieldset": {
                                borderColor: "#e0e0e0",
                            },
                            "&:hover fieldset": {
                                borderColor: message?.type === "error" ? "#d32f2f" : "#0066cc",
                            },
                            "&.Mui-error fieldset": {
                                borderColor: message?.type === "info" ? "#0066cc" : undefined,
                            }
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: "none",
                        color: "#0066cc",
                        border: "1px solid #0066cc",
                        borderRadius: "8px",
                        px: 3,
                        fontWeight: "500",
                        "&:hover": {
                            backgroundColor: "rgba(0, 102, 204, 0.04)",
                            borderColor: "#003366",
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleReset}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 4,
                        fontWeight: "bold",
                        background: "linear-gradient(135deg, #0066cc 0%, #003366 100%)",
                        boxShadow: "0 4px 15px rgba(0, 102, 204, 0.4)",
                        transition: "all 0.3s",
                        minWidth: "100px",
                        "&:hover": {
                            background: "linear-gradient(135deg, #0066cc 0%, #003366 100%)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 20px rgba(0, 102, 204, 0.6)",
                        },
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Reset"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ForgotPasswordPage;